import React, { useEffect, useRef } from "react";
import { withFormik, InjectedFormikProps } from "formik";
import { connect } from "react-redux";
import { Form, Alert } from "reactstrap";
import Button from "../../../../components/common/button";
import { Save, AlertCircle } from "react-feather";
import { IIngredientAddFormData, IIngredientFormData } from "../../../../interfaces/ingredient";
import IngredientFormElements from "./ingredientFormElements";
import { formatProductFormData } from "../../../../utils/product";
import { formatIngredientFormData } from "../../../../utils/ingredient";
import ingredientService from "../../../../services/ingredientService";
import { IApplicationState } from "../../../../redux/reducers";
import { IngredientValidation } from "../../../../validations/ingredientValidation";

interface IAddIngredientFormProps {
	toggleForm: () => void;
	profile?: any;
	add: (ingredientData: IIngredientAddFormData, profile: any) => Promise<any>;
	ingredientPreferance?: any;
}

const AddIngredientForm: React.FC<InjectedFormikProps<IAddIngredientFormProps, any>> = (props) => {
	const { errors, touched, isValid, submitCount } = props;
	const formRef = useRef<HTMLDivElement>(null);
	const hasErrors = Object.keys(errors).length > 0;
	const hasTouchedErrors = Object.keys(touched).some((key) => touched[key] && errors[key]);

	// Scroll to first error on submit attempt
	useEffect(() => {
		if (submitCount > 0 && hasTouchedErrors && formRef.current) {
			const firstErrorField = formRef.current.querySelector(
				'[aria-invalid="true"]'
			) as HTMLElement;
			if (firstErrorField) {
				firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
				firstErrorField.focus();
			}
		}
	}, [submitCount, hasTouchedErrors]);

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		// Validate all fields on submit
		const validationErrors = await props.validateForm();
		if (Object.keys(validationErrors).length > 0) {
			props.setTouched(
				Object.keys(validationErrors).reduce((acc, key) => {
					acc[key] = true;
					return acc;
				}, {} as any)
			);
		} else {
			props.handleSubmit(e);
		}
	};

	return (
		<div ref={formRef}>
			<Form className="product-form" onSubmit={handleSubmit}>
				{submitCount > 0 && hasTouchedErrors && (
					<Alert
						color="danger"
						style={{
							marginBottom: "1rem",
							display: "flex",
							alignItems: "center",
							padding: "0.75rem 1rem"
						}}
					>
						<AlertCircle size={16} style={{ marginRight: "0.5rem", flexShrink: 0 }} />
						<span>
							Please fix the errors below before submitting. {Object.keys(errors).length}{" "}
							{Object.keys(errors).length === 1 ? "field has" : "fields have"} validation
							errors.
						</span>
					</Alert>
				)}
				<IngredientFormElements ingredientPreferance={props.ingredientPreferance} {...props} />
				<br />
				<hr />
				<span className="save">
					<Button
						className="color-white mr-2"
						type="submit"
						color="primary"
						loading={props.isSubmitting}
						disabled={props.isSubmitting}
					>
						<Save size={18} className="mr-2" />
						Add
					</Button>
				</span>
			</Form>
		</div>
	);
};

const WrappedForm = withFormik<IAddIngredientFormProps, IIngredientAddFormData>({
	mapPropsToValues: () => {
		return {
			name: "",
			jf_display_name: "",
			price_per_unit: "",
			food_group: [],
			available_markets: [],
			country_data: [],
			df_energy_kcal: "",
			df_protein: "",
			df_carbohydrate: "",
			df_fiber: "",
			df_sugar: "",
			df_calcium: "",
			df_vitamin_d2_d3: "",
			df_vitamin_c: "",
			df_cholesterol: "",
			df_fatty_acid_total_saturated: "",
			df_fatty_acid_total_trans: "",
			df_iron: "",
			df_potassium: "",
			df_sodium: "",
			datasheets: [],
			allergens: [],
			certifications: [],
			claim: "",
			regulation: "",
			upcycled: [],
			notes: "",
			plant_based: "No",
			is_upcycled: false,
			is_global: false,
			price_unit: "",
			water_footprint: "",
			greenhouse_gas_emissions: ""
		};
	},
	handleSubmit: async function (values, { props, resetForm, setSubmitting }) {
		const formattedData = formatIngredientFormData(values, null);
		try {
			await props.add(formattedData, props.profile);
			props.toggleForm();
			resetForm();
		} catch (error) {
			// Error is already handled by the service (this.handleError)
			// Just ensure form submission state is reset
			setSubmitting(false);
		}
	},
	validationSchema: IngredientValidation
});
const mapStateToProps = (state: IApplicationState) => ({
	profile: state.user.profile,
	ingredientPreferance: state.preference.preferences
});
const mapDispatchToProps = {
	add: (ingredientData: IIngredientAddFormData, profile: any) =>
		ingredientService.addIngredient({
			ingredients: [
				{
					...ingredientData,
					ingredient: ingredientData.jf_display_name,
					price: ingredientData.price_per_unit?.toString(),
					type_of_upload: "manual",
					manufacturer: {
						website: "",
						email: profile.email,
						image_url: "",
						name: profile.company.name,
						description: "",
						id: profile.id,
						address: "",
						phone: profile.phone_number
					}
				}
			]
		}),
	removeIngredient: (id: string) => ingredientService.removeIngredient(id)
};
export default connect(mapStateToProps, mapDispatchToProps)(WrappedForm(AddIngredientForm));
